import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaTwitter,
  FaGithub,
  FaInstagram,
  FaWhatsapp,
  FaArrowUp,
  FaCheckCircle,
} from 'react-icons/fa';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubscribed(true);
      setEmail('');
      setTimeout(() => setIsSubscribed(false), 3000);
    }, 1100);
  };

  const socials = [
    { href: 'https://x.com/MonkeyyTech/', icon: FaTwitter, label: 'Twitter / X', color: '#1DA1F2' },
    { href: 'https://github.com/monkeytech', icon: FaGithub, label: 'GitHub', color: '#333333' },
    { href: 'https://www.instagram.com/monkeyy.tech?igsh=MTFsZzRuZjd4cTZkMQ==', icon: FaInstagram, label: 'Instagram', color: '#E1306C' },
    { href: 'https://wa.me/919316036603', icon: FaWhatsapp, label: 'WhatsApp', color: '#25D366' },
  ];

  const socialVariants = { hover: { y: -2, scale: 1.08 }, tap: { scale: 0.95 } };
  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <footer className="relative mt-auto font-inter">
      {/* Light/Dark background layers */}
      <div className="absolute inset-0 -z-10">
        {/* Light mode background */}
        <div className="absolute inset-0 block dark:hidden bg-gradient-to-br from-[#F6FAFF] via-[#EEF3F9] to-white" />
        {/* Dark mode background */}
        <div className="absolute inset-0 hidden dark:block bg-gradient-to-br from-[#0B1220] via-[#0B1325] to-black" />

        {/* Orbs */}
        <div
          className="absolute -top-20 -left-20 h-[28rem] w-[28rem] rounded-full blur-3xl"
          style={{ background: 'radial-gradient(closest-side, rgba(31,165,80,0.20), transparent)' }}
        />
        <div
          className="absolute bottom-[-6rem] right-[-6rem] h-[24rem] w-[24rem] rounded-full blur-3xl"
          style={{ background: 'radial-gradient(closest-side, rgba(6,182,212,0.18), transparent)' }}
        />
        {/* Grid mask */}
        <div
          className="absolute inset-0 opacity-[0.06] [mask-image:linear-gradient(to_top,transparent,black_16%,black_84%,transparent)]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(0,0,0,.22) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,.22) 1px, transparent 1px)',
            backgroundSize: '44px 44px',
          }}
        />
      </div>

      {/* Top gradient strip */}
      <div className="pointer-events-none select-none" aria-hidden="true">
        <svg className="w-full h-10 -mt-10 text-black/0" viewBox="0 0 1440 80" preserveAspectRatio="none">
          <path d="M0,64 C240,16 480,16 720,48 C960,80 1200,80 1440,48 L1440,0 L0,0 Z" fill="url(#g)"></path>
          <defs>
            <linearGradient id="g" x1="0" x2="1">
              <stop offset="0%" stopColor="var(--brand, #1FA550)" />
              <stop offset="100%" stopColor="var(--accent, #06B6D4)" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Shell */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        <div
          className="p-[1.6px] rounded-2xl"
          style={{ background: 'linear-gradient(135deg, rgba(31,165,80,0.55), rgba(6,182,212,0.55))' }}
        >
          <div
            className="rounded-2xl px-6 py-10 sm:px-10"
            /* Light and dark surface */
            style={{
              background: 'rgba(255,255,255,0.78)',
              border: '1px solid rgba(2,6,23,0.10)',
              boxShadow: '0 26px 64px rgba(2,6,23,0.16)',
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-10 text-gray-900 dark:text-white">
              {/* Brand + Socials */}
              <div className="col-span-1">
                <div className="flex items-center gap-3">
                  <img
                    src="/images/header.png"
                    alt="MonkeyTech"
                    className="h-10 w-10 rounded-lg object-cover"
                    onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/40?text=MT'; }}
                  />
                  <h3
                    className="text-2xl font-extrabold tracking-tight bg-clip-text text-transparent"
                    style={{ backgroundImage: 'linear-gradient(90deg, var(--brand, #1FA550), var(--accent, #06B6D4))' }}
                  >
                    MonkeyTech
                  </h3>
                </div>
                <p className="mt-4 text-sm text-gray-700 dark:text-white/80">
                  Empowering innovation through modern web, apps, AI agents, chatbots & BI dashboards.
                </p>

                <div className="mt-5 flex items-center gap-3">
                  {socials.map(({ href, icon: Icon, label, color }) => (
                    <motion.a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      variants={socialVariants}
                      whileHover="hover"
                      whileTap="tap"
                      title={label}
                      className="group inline-flex h-10 w-10 items-center justify-center rounded-full ring-1 transition bg-white/70 dark:bg-white/10"
                      style={{ borderColor: 'rgba(2,6,23,0.12)' }}
                    >
                      <Icon size={18} className="transition-transform group-hover:scale-110" style={{ color }} />
                    </motion.a>
                  ))}
                </div>
              </div>

              {/* Quick Links */}
              <nav className="col-span-1" aria-label="Quick Links">
                <h4 className="text-sm font-semibold tracking-wider uppercase text-gray-800 dark:text-white/90">Quick Links</h4>
                <ul className="mt-4 space-y-2 text-sm">
                  {[
                    { href: '/about', text: 'About Us' },
                    { href: '/services', text: 'Services' },
                    { href: '/portfolio', text: 'Portfolio' },
                    { href: '/contact', text: 'Contact' },
                  ].map(({ href, text }) => (
                    <li key={text}>
                      <a
                        href={href}
                        className="text-gray-700 dark:text-white/80 hover:text-black dark:hover:text-white transition inline-flex items-center gap-2"
                      >
                        <span
                          className="h-[2px] w-2 rounded-full"
                          style={{ background: 'linear-gradient(90deg, var(--brand, #1FA550), var(--accent, #06B6D4))' }}
                        />
                        {text}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>

              {/* Services */}
              <nav className="col-span-1" aria-label="Services">
                <h4 className="text-sm font-semibold tracking-wider uppercase text-gray-800 dark:text-white/90">Services</h4>
                <ul className="mt-4 space-y-2 text-sm">
                  {[
                    { href: '/services#Web', text: 'Website Development' },
                    { href: '/services#App', text: 'Application Development' },
                    { href: '/services#AI', text: 'AI & Agentic Automation' },
                    { href: '/services#Data', text: 'Power BI & Dashboards' },
                  ].map(({ href, text }) => (
                    <li key={text}>
                      <a
                        href={href}
                        className="text-gray-700 dark:text-white/80 hover:text-black dark:hover:text-white transition inline-flex items-center gap-2"
                      >
                        <span
                          className="h-[2px] w-2 rounded-full"
                          style={{ background: 'linear-gradient(90deg, var(--brand, #1FA550), var(--accent, #06B6D4))' }}
                        />
                        {text}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>

              {/* Newsletter */}
              <div className="col-span-1">
                <h4 className="text-sm font-semibold tracking-wider uppercase text-gray-800 dark:text-white/90">Newsletter</h4>
                <p className="mt-3 text-sm text-gray-700 dark:text-white/80">Stay updated with our latest drops & offers.</p>

                <form onSubmit={handleSubmit} className="mt-4 flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                      className="w-full rounded-xl bg-white text-gray-900 placeholder-gray-500 px-4 py-3 outline-none ring-1 ring-gray-300 focus:ring-2 focus:ring-[var(--brand,#1FA550)] transition dark:bg-white/10 dark:text-white dark:placeholder-white/60 dark:ring-white/10"
                    />
                    <span
                      className="pointer-events-none absolute inset-y-0 right-3 my-auto w-20 h-6 [mask-image:linear-gradient(90deg,transparent,black,transparent)] opacity-20"
                      style={{ background: 'linear-gradient(90deg, rgba(255,255,255,.1), rgba(0,0,0,.25), rgba(255,255,255,.1))' }}
                    />
                  </div>
                  <motion.button
                    type="submit"
                    whileHover={{ scale: isSubmitting ? 1 : 1.03 }}
                    whileTap={{ scale: isSubmitting ? 1 : 0.97 }}
                    disabled={isSubmitting}
                    className="relative rounded-xl px-5 py-3 font-semibold text-black disabled:opacity-70"
                    style={{ background: 'linear-gradient(135deg, var(--brand, #1FA550), var(--accent, #06B6D4))' }}
                  >
                    {isSubmitting ? 'Subscribing…' : 'Subscribe'}
                  </motion.button>
                </form>

                <AnimatePresence>
                  {isSubscribed && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      className="mt-3 inline-flex items-center gap-2 text-emerald-700 dark:text-green-300 text-sm"
                    >
                      <FaCheckCircle className="text-emerald-600 dark:text-green-400" />
                      Thanks for subscribing!
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Contact block */}
                <div
                  className="mt-6 rounded-xl p-4 ring-1 bg-white/60 dark:bg-white/5"
                  style={{ borderColor: 'rgba(2,6,23,0.10)' }}
                >
                  <div className="text-xs font-semibold tracking-wider uppercase text-gray-700 dark:text-white/70">Contact</div>
                  <div className="mt-2 text-sm text-gray-800 dark:text-white/85">
                    <a href="mailto:hello@monkeytech.com" className="hover:underline">hello@monkeytech.com</a><br />
                    <a href="tel:+919316036603" className="hover:underline">+91 93160 36603</a>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom bar */}
            <div className="mt-10 pt-6 border-t border-gray-200 dark:border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-gray-700 dark:text-white/80">
              <p className="text-xs sm:text-sm">
                © {new Date().getFullYear()} MonkeyTech — All rights reserved.
              </p>
              <div className="flex items-center gap-6 text-xs sm:text-sm">
                <a href="/privacy" className="hover:opacity-100 opacity-90">Privacy</a>
                <span className="opacity-40">•</span>
                <a href="/terms" className="hover:opacity-100 opacity-90">Terms</a>
                <span className="opacity-40">•</span>
                <button
                  onClick={scrollTop}
                  className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-black"
                  style={{ background: 'linear-gradient(135deg, var(--brand, #1FA550), var(--accent, #06B6D4))' }}
                >
                  <FaArrowUp /> Top
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Defaults for CSS vars */}
      <style>{`
        .font-inter{font-family:Inter,ui-sans-serif,system-ui,-apple-system,'Segoe UI',Roboto,Helvetica,Arial}
        :root{--brand:#1FA550;--accent:#06B6D4}
      `}</style>
    </footer>
  );
}
